import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { submitLead } from "@/lib/api";
import { RESIDENTIAL, COMMERCIAL_CATEGORIES } from "@/data/site";

const RES_NAMES = RESIDENTIAL.map((s) => s.name);
const FACILITY_TYPES = COMMERCIAL_CATEGORIES.flatMap((c) => c.items);

const EMPTY = {
  name: "", email: "", phone: "", service_category: "residential",
  service_type: "", frequency: "", preferred_date: "", address: "", message: "",
};

export const LeadForm = ({ type = "contact", onSuccess }) => {
  const isProposal = type === "proposal";
  const [form, setForm] = useState({ ...EMPTY, service_category: isProposal ? "commercial" : "residential" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try {
      await submitLead({ type, ...form });
      toast.success(
        type === "booking" ? "Booking request received! Our team will confirm shortly."
        : isProposal ? "Proposal request received! We'll build a custom plan and reach out."
        : type === "contact" ? "Message sent! We'll be in touch soon."
        : type === "quote" ? "Quote request received! We'll send your quote shortly."
        : "Request received! We'll be in touch shortly."
      );
      setForm({ ...EMPTY, service_category: isProposal ? "commercial" : "residential" });
      onSuccess?.();
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    } finally { setLoading(false); }
  };

  const cta = type === "booking" ? "Request Booking" : isProposal ? "Request a Quote" : type === "quote" ? "Request My Quote" : "Send Message";

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`lead-form-${type}`}>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="lf-name" className="text-navy/70 text-xs uppercase tracking-widest">Name</Label>
          <Input id="lf-name" data-testid="lead-name-input" className="h-12 bg-white focus-visible:ring-leaf"
            value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lf-email" className="text-navy/70 text-xs uppercase tracking-widest">Email</Label>
          <Input id="lf-email" type="email" data-testid="lead-email-input" className="h-12 bg-white focus-visible:ring-leaf"
            value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="lf-phone" className="text-navy/70 text-xs uppercase tracking-widest">Phone</Label>
          <Input id="lf-phone" data-testid="lead-phone-input" className="h-12 bg-white focus-visible:ring-leaf"
            value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(703) 555-0100" />
        </div>
        {isProposal ? (
          <div className="space-y-1.5">
            <Label htmlFor="lf-company" className="text-navy/70 text-xs uppercase tracking-widest">Company / Organization</Label>
            <Input id="lf-company" data-testid="lead-company-input" className="h-12 bg-white focus-visible:ring-leaf"
              value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Company name" />
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label className="text-navy/70 text-xs uppercase tracking-widest">Space Type</Label>
            <Select value={form.service_category} onValueChange={(v) => set("service_category", v)}>
              <SelectTrigger className="h-12 bg-white" data-testid="lead-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="residential">Residential</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {isProposal && (
        <div className="space-y-1.5">
          <Label className="text-navy/70 text-xs uppercase tracking-widest">Facility Type</Label>
          <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
            <SelectTrigger className="h-12 bg-white" data-testid="lead-facility-select"><SelectValue placeholder="Choose facility type" /></SelectTrigger>
            <SelectContent>
              {FACILITY_TYPES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {type === "booking" && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-navy/70 text-xs uppercase tracking-widest">Service</Label>
            <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
              <SelectTrigger className="h-12 bg-white" data-testid="lead-service-select"><SelectValue placeholder="Choose a service" /></SelectTrigger>
              <SelectContent>{RES_NAMES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lf-date" className="text-navy/70 text-xs uppercase tracking-widest">Preferred Date</Label>
            <Input id="lf-date" type="date" data-testid="lead-date-input" className="h-12 bg-white focus-visible:ring-leaf"
              value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} />
          </div>
        </div>
      )}

      {!isProposal && (
        <div className="space-y-1.5">
          <Label htmlFor="lf-addr" className="text-navy/70 text-xs uppercase tracking-widest">City / Area (DC · MD · Northern VA)</Label>
          <Input id="lf-addr" data-testid="lead-address-input" className="h-12 bg-white focus-visible:ring-leaf"
            value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="e.g. Arlington, VA" />
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="lf-msg" className="text-navy/70 text-xs uppercase tracking-widest">
          {type === "contact" ? "How can we help?" : isProposal ? "Tell us about your facility & needs" : "Anything else?"}
        </Label>
        <Textarea id="lf-msg" data-testid="lead-message-input" className="min-h-[96px] bg-white focus-visible:ring-leaf"
          value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={isProposal ? "Square footage, frequency, special protocols…" : "Tell us about your space, pets, priorities…"} />
      </div>

      <button type="submit" disabled={loading} data-testid="lead-submit-btn"
        className="group w-full py-4 rounded-full bg-leaf text-white font-medium text-base flex items-center justify-center gap-2 transition-transform duration-200 hover:scale-[1.02] active:scale-100 disabled:opacity-60">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>}
      </button>
    </form>
  );
};
